import { useState, createElement } from 'react';
import { DragStartEvent, DragEndEvent, DragOverEvent } from '@dnd-kit/core';
import { FolderContent } from '@/api/generatedApi';
import { useDndSensors } from '@/components/dnd';
import { getContentParentFolderId, isFolder } from '@/utils/folders';
import { useContentDrag } from './ContentDragContext';
import { ContentDragPreview } from './ContentDragPreview';

interface UseFolderDndOptions {
  onMoveContents: (contents: FolderContent[], targetFolderId: string, targetFolderName?: string) => void;
  onDropOnHiddenFolders?: (contents: FolderContent[]) => void;
}

export const useFolderDnd = ({ onMoveContents, onDropOnHiddenFolders }: UseFolderDndOptions) => {
  const { currentFolderId, setDraggedContents, setDropTargetFolderId } = useContentDrag();
  const [draggedContentsData, setDraggedContentsData] = useState<FolderContent[]>([]);

  const sensors = useDndSensors();

  const handleDragStart = (event: DragStartEvent) => {
    const dragData = event.active.data.current as { contents?: FolderContent[] } | undefined;
    if (dragData?.contents && dragData.contents.length > 0) {
      setDraggedContents(dragData.contents);
      setDraggedContentsData(dragData.contents);
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;
    const dropData = over?.data.current as { type: string; folderId?: string } | undefined;

    switch (dropData?.type) {
      case 'folder-drop-target': {
        setDropTargetFolderId(dropData.folderId);
        break;
      }
      case 'root-drop-target': {
        setDropTargetFolderId('root');
        break;
      }
      case 'hidden-folders-drop-target': {
        setDropTargetFolderId('hidden-folders');
        break;
      }
      default: {
        setDropTargetFolderId(undefined);
        break;
      }
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    const dragData = active.data.current as { contents?: FolderContent[] } | undefined;
    const dropData = over?.data.current as { type: string; folderId?: string; folderName?: string } | undefined;

    setDraggedContents([]);
    setDraggedContentsData([]);
    setDropTargetFolderId(undefined);

    if (!over) return;

    if (dropData?.type === 'hidden-folders-drop-target') {
      if (dragData?.contents && dragData.contents.length > 0 && onDropOnHiddenFolders) {
        onDropOnHiddenFolders(dragData.contents);
      }
      return;
    }

    let targetFolderId: string;
    let targetFolderName: string | undefined;
    if (dropData?.type === 'root-drop-target') {
      targetFolderId = 'root';
    } else if (dropData?.type === 'folder-drop-target' && dropData.folderId) {
      targetFolderId = dropData.folderId;
      targetFolderName = dropData.folderName;
    } else {
      return;
    }

    if (dragData?.contents && dragData.contents.length > 0) {
      const contentsToMove = dragData.contents.filter(content => {
        const parentId = getContentParentFolderId(content);
        if (isFolder(content) && targetFolderId === content.id) return false;
        if (targetFolderId === parentId) return false;
        if (targetFolderId === 'root' && parentId === undefined) return false;
        return true;
      });

      if (contentsToMove.length > 0) {
        onMoveContents(contentsToMove, targetFolderId, targetFolderName);
      }
    }
  };

  const handleDragCancel = () => {
    setDraggedContents([]);
    setDraggedContentsData([]);
    setDropTargetFolderId(undefined);
  };

  const dragOverlay =
    draggedContentsData.length > 0 ? createElement(ContentDragPreview, { contents: draggedContentsData }) : undefined;

  return {
    sensors,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
    dragOverlay,
    isDragging: draggedContentsData.length > 0,
    currentFolderId,
  };
};
